import { motion } from "framer-motion";
import {
    Phone,
    MessageCircle,
    Hotel,
    Martini,
    UtensilsCrossed,
    Ship,
    Clock3,
    Sparkles,
} from "lucide-react";
import { useLang, PHONE_TEL, waLink } from "@/i18n";
import { SEO } from "@/components/SEO";
import { Marquee } from "@/components/Marquee";
import { Reveal } from "@/components/Reveal";

const IMGS = {
    hero: "https://images.pexels.com/photos/12861106/pexels-photo-12861106.jpeg?auto=compress&cs=tinysrgb&w=1920",
    marina: "https://images.pexels.com/photos/14909610/pexels-photo-14909610.jpeg?auto=compress&cs=tinysrgb&w=940",
    chauffeur: "https://images.pexels.com/photos/7594130/pexels-photo-7594130.jpeg?auto=compress&cs=tinysrgb&w=940",
};

const copy = {
    tr: {
        eyebrow: "GİRNE MERKEZ • 7/24 VIP TAKSİ",
        title1: "Girne İçi",
        title2: "VIP Taksi",
        sub: "Liman, oteller, casinolar ve restoranlar... Girne'nin neresinde olursanız olun, Mercedes E-Class konforu dakikalar içinde kapınızda.",
        hotelsLabel: "OTEL & RESORT SERVİSİ",
        hotelsTitle: "Öncelikli Alım Noktaları",
        hotelsSub: "Otelinizin lobisinden alınır, istediğiniz noktaya bırakılırsınız. Otelinize dokunun, rezervasyon mesajınız hazırlansın.",
        hotels: [
            "Merit Royal & Park",
            "Cratos Premium",
            "Elexus Hotel",
            "Lord's Palace",
            "The Colony",
            "Kaya Palazzo",
            "Savoy Ottoman Palace",
            "Oscar Resort",
            "Acapulco Resort",
            "Kaya Artemis",
        ],
        lifeLabel: "GİRNE YAŞAMI",
        lifeTitle: "Gece Hayatı, Yemek, Liman — Hep Yanınızdayız",
        life: [
            { icon: Martini, t: "Gece Hayatı", d: "Casino ve eğlence mekânlarından güvenli dönüş. Gecenin her saati tek telefon uzağınızdayız." },
            { icon: UtensilsCrossed, t: "Restoran Transferi", d: "Akşam yemeğine şık bir varış. Rezervasyon saatinize göre planlanan çift yönlü servis." },
            { icon: Ship, t: "Liman & Marina", d: "Girne Limanı ve marina bölgesinden otellere, plajlara ve tarihi noktalara hızlı ulaşım." },
            { icon: Clock3, t: "Saatlik Tahsis", d: "Alışveriş, iş görüşmeleri ve ada turu için şoförlü araç saatlik olarak emrinizde." },
        ],
        hotelMsg: (h) => `Merhaba, ${h} otelinden taksi talep ediyorum. Konum ve detayları paylaşmak istiyorum.`,
        ctaTitle: "Girne'de Araca mı İhtiyacınız Var?",
        ctaSub: "Şu an arayın, dakikalar içinde yanınızda olalım.",
    },
    en: {
        eyebrow: "KYRENIA CENTRE • 24/7 VIP TAXI",
        title1: "Inner-Kyrenia",
        title2: "VIP Taxi",
        sub: "Harbour, hotels, casinos and restaurants... Wherever you are in Kyrenia, Mercedes E-Class comfort is at your door within minutes.",
        hotelsLabel: "HOTEL & RESORT SERVICE",
        hotelsTitle: "Priority Pickup Points",
        hotelsSub: "Picked up from your hotel lobby, dropped at any point you wish. Tap your hotel and your booking message is ready.",
        hotels: [
            "Merit Royal & Park",
            "Cratos Premium",
            "Elexus Hotel",
            "Lord's Palace",
            "The Colony",
            "Kaya Palazzo",
            "Savoy Ottoman Palace",
            "Oscar Resort",
            "Acapulco Resort",
            "Kaya Artemis",
        ],
        lifeLabel: "KYRENIA LIFE",
        lifeTitle: "Nightlife, Dining, Harbour — Always By Your Side",
        life: [
            { icon: Martini, t: "Nightlife", d: "A safe ride back from casinos and nightclubs. One call away at any hour of the night." },
            { icon: UtensilsCrossed, t: "Restaurant Transfers", d: "Arrive at dinner in style. Round-trip service planned around your reservation time." },
            { icon: Ship, t: "Harbour & Marina", d: "Fast rides from Kyrenia Harbour and the marina to hotels, beaches and historic sights." },
            { icon: Clock3, t: "Hourly Hire", d: "A chauffeured car at your disposal by the hour for shopping, meetings and island tours." },
        ],
        hotelMsg: (h) => `Hello, I would like a taxi from ${h} hotel. I would like to share my location and details.`,
        ctaTitle: "Need a Car in Kyrenia?",
        ctaSub: "Call now — we will be by your side in minutes.",
    },
};

export default function GirneTaksi() {
    const { lang, t } = useLang();
    const c = copy[lang];

    return (
        <main>
            <SEO title={t("seo.girne.title")} description={t("seo.girne.desc")} path="/girne-taksi" />

            <section className="relative min-h-[85svh] flex items-end overflow-hidden" data-testid="girne-hero">
                <div className="absolute inset-0">
                    <img src={IMGS.hero} alt="Girne Limanı VIP Taksi" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#08080A]/80 via-[#08080A]/50 to-[#08080A]" />
                </div>
                <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 w-full pb-24 pt-40">
                    <span className="ff-sans text-[11px] md:text-xs tracking-[0.4em] text-[#D4AF37] uppercase block mb-5" data-testid="girne-eyebrow">
                        {c.eyebrow}
                    </span>
                    <h1 className="ff-serif text-4xl sm:text-5xl lg:text-7xl text-[#F7F5F0] leading-[1.08] tracking-tight" data-testid="girne-title">
                        <span className="block overflow-hidden pb-1">
                            <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.95, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}>
                                {c.title1}
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-1">
                            <motion.span className="block italic text-gold-gradient" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.95, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}>
                                {c.title2}
                            </motion.span>
                        </span>
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8, duration: 0.8 }}
                        className="mt-6 max-w-xl text-base md:text-lg text-zinc-400 font-light ff-sans leading-relaxed"
                        data-testid="girne-subtitle"
                    >
                        {c.sub}
                    </motion.p>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }} className="mt-9 flex flex-col sm:flex-row gap-4">
                        <a href={PHONE_TEL} data-testid="girne-call-button" className="gold-glow-hover animate-soft-pulse flex items-center justify-center gap-2.5 rounded-full bg-[#D4AF37] text-[#08080A] font-semibold px-8 py-4 ff-sans text-sm tracking-wide">
                            <Phone size={16} /> +90 533 856 7070
                        </a>
                        <a href={waLink(t("hero.waMsg"))} target="_blank" rel="noopener noreferrer" data-testid="girne-whatsapp-button" className="gold-glow-hover flex items-center justify-center gap-2.5 rounded-full border border-[#D4AF37]/50 text-[#E6C687] px-8 py-4 ff-sans text-sm tracking-wide bg-[#08080A]/40 backdrop-blur-sm">
                            <MessageCircle size={16} /> WhatsApp
                        </a>
                    </motion.div>
                </div>
            </section>

            <Marquee />

            <section className="py-24" data-testid="girne-hotels">
                <div className="max-w-7xl mx-auto px-5 lg:px-8">
                    <Reveal className="max-w-2xl mb-12">
                        <span className="ff-sans text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] block mb-4">{c.hotelsLabel}</span>
                        <h2 className="ff-serif text-3xl md:text-5xl text-[#F7F5F0] tracking-tight">{c.hotelsTitle}</h2>
                        <p className="mt-4 text-base text-zinc-400 font-light ff-sans">{c.hotelsSub}</p>
                    </Reveal>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {c.hotels.map((h, i) => (
                            <Reveal key={h} delay={(i % 5) * 0.06}>
                                <a
                                    href={waLink(c.hotelMsg(h))}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`hotel-chip-${i}`}
                                    className="gold-glow-hover flex flex-col items-center gap-3 rounded-2xl border border-white/8 bg-[#0F0F13] px-4 py-7 text-center group"
                                >
                                    <Hotel size={20} className="text-[#D4AF37]" />
                                    <span className="ff-sans text-sm text-zinc-300 group-hover:text-[#E6C687] transition-colors leading-snug">{h}</span>
                                </a>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-24 bg-[#0A0A0E] border-y border-white/5" data-testid="girne-life">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
                    <div>
                        <Reveal>
                            <span className="ff-sans text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] block mb-4">{c.lifeLabel}</span>
                            <h2 className="ff-serif text-3xl md:text-5xl text-[#F7F5F0] tracking-tight leading-[1.15]">{c.lifeTitle}</h2>
                        </Reveal>
                        <div className="mt-10 space-y-6">
                            {c.life.map((item, i) => {
                                const Icon = item.icon;
                                return (
                                    <Reveal key={item.t} delay={i * 0.08}>
                                        <div className="flex gap-5 items-start" data-testid={`life-item-${i}`}>
                                            <div className="shrink-0 w-11 h-11 rounded-full border border-[#D4AF37]/30 flex items-center justify-center">
                                                <Icon size={18} className="text-[#D4AF37]" />
                                            </div>
                                            <div>
                                                <h3 className="ff-serif text-xl text-[#F7F5F0]">{item.t}</h3>
                                                <p className="text-sm text-zinc-400 font-light ff-sans leading-relaxed mt-1">{item.d}</p>
                                            </div>
                                        </div>
                                    </Reveal>
                                );
                            })}
                        </div>
                    </div>
                    <Reveal delay={0.15}>
                        <div className="spotlight-frame">
                            <img src={IMGS.chauffeur} alt="VIP Şoförlü Araç Hizmeti" loading="lazy" className="relative z-10 rounded-2xl border border-[#D4AF37]/20 object-cover w-full h-[480px]" />
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="py-28 text-center grain-overlay" data-testid="girne-cta">
                <div className="max-w-2xl mx-auto px-5">
                    <Reveal>
                        <Sparkles size={20} className="mx-auto text-[#D4AF37] mb-5" />
                        <h2 className="ff-serif text-3xl md:text-5xl text-[#F7F5F0] tracking-tight">{c.ctaTitle}</h2>
                        <p className="mt-4 text-base md:text-lg text-zinc-400 font-light ff-sans">{c.ctaSub}</p>
                        <div className="mt-9 flex flex-col sm:flex-row justify-center gap-4">
                            <a href={PHONE_TEL} data-testid="girne-cta-call" className="gold-glow-hover animate-soft-pulse flex items-center justify-center gap-2.5 rounded-full bg-[#D4AF37] text-[#08080A] font-semibold px-9 py-4 ff-sans text-sm tracking-wide">
                                <Phone size={16} /> +90 533 856 7070
                            </a>
                            <a href={waLink(t("hero.waMsg"))} target="_blank" rel="noopener noreferrer" data-testid="girne-cta-whatsapp" className="gold-glow-hover flex items-center justify-center gap-2.5 rounded-full border border-[#D4AF37]/50 text-[#E6C687] px-9 py-4 ff-sans text-sm tracking-wide">
                                <MessageCircle size={16} /> WhatsApp
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
